import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { EmptyState, PageHeader } from '@/components/page-header';
import { ApprovalActions } from '@/components/portal/approval-actions';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatAmount, formatDate, formatDateTime } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import {
  approvalState,
  documentPath,
  isClientRole,
  useApprovals,
  useOrgRole,
  type Approval,
} from '@/lib/portal';
import { cn } from '@/lib/utils';

type Tab = 'pending' | 'decided';

export const Route = createFileRoute('/documents/approvals')({
  component: Approvals,
  validateSearch: (s: Record<string, unknown>): { tab?: Tab } =>
    s['tab'] === 'decided' ? { tab: 'decided' } : {},
});

/**
 * Approvals (document 06, "Client portal"): draft invoices and bills waiting for a decision, the
 * client's or Outss's, with approve and reject with a comment; History lists the decisions.
 */
function Approvals() {
  const orgId = useOrgId();
  const role = useOrgRole();
  const staff = role !== null && !isClientRole(role);
  const { tab = 'pending' } = Route.useSearch();
  const navigate = useNavigate();
  const list = useApprovals(tab);
  if (!orgId)
    return <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />;
  const rows = list.data ?? [];
  const mine = rows.filter((a) => a.canDecide);
  const others = rows.filter((a) => !a.canDecide);
  return (
    <>
      <PageHeader
        title="Approvals"
        description={
          staff
            ? 'Drafts the client submitted for your approval, and drafts you asked the client to approve.'
            : 'Invoices and bills your accountant asked you to approve, and drafts you submitted to them.'
        }
      />
      <div className="mb-3 flex gap-2" role="tablist" aria-label="Approvals">
        {(
          [
            ['pending', 'Waiting'],
            ['decided', 'History'],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            role="tab"
            aria-selected={tab === k}
            className={cn(
              'rounded-full border px-3 py-1 text-sm',
              tab === k ? 'border-primary bg-primary/10 text-primary' : 'bg-card',
            )}
            onClick={() =>
              void navigate({
                to: '/documents/approvals',
                search: k === 'decided' ? { tab: k } : {},
              })
            }
          >
            {label}
          </button>
        ))}
      </div>
      {list.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : list.isError ? (
        <EmptyState title="Could not load approvals" hint={list.error.message} />
      ) : !rows.length ? (
        <EmptyState
          title={tab === 'pending' ? 'Nothing is waiting for approval' : 'No decisions yet'}
          hint={
            staff
              ? 'Use "Ask the client to approve" on a draft invoice or bill.'
              : 'Your accountant will ask you here when a draft needs your approval.'
          }
        />
      ) : tab === 'pending' ? (
        <div className="space-y-6">
          <Section
            title="Waiting for you"
            rows={mine}
            staff={staff}
            empty="Nothing needs your decision."
          />
          <Section
            title={
              staff ? 'Waiting for the client, or ready to post' : 'Waiting for your accountant'
            }
            rows={others}
            staff={staff}
            empty="Nothing here."
          />
        </div>
      ) : (
        <ApprovalTable rows={rows} staff={staff} history />
      )}
    </>
  );
}

function Section({
  title,
  rows,
  staff,
  empty,
}: {
  title: string;
  rows: Approval[];
  staff: boolean;
  empty: string;
}) {
  return (
    <section>
      <h2 className="mb-2 text-base font-semibold">
        {title} <span className="font-normal text-muted-foreground">({rows.length})</span>
      </h2>
      {rows.length ? (
        <ApprovalTable rows={rows} staff={staff} />
      ) : (
        <p className="rounded-lg border bg-card px-4 py-3 text-sm text-muted-foreground">{empty}</p>
      )}
    </section>
  );
}

function ApprovalTable({
  rows,
  staff,
  history,
}: {
  rows: Approval[];
  staff: boolean;
  history?: boolean;
}) {
  return (
    <div className="rounded-lg border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[150px]">Document</TableHead>
            <TableHead>Customer or vendor</TableHead>
            <TableHead className="w-[100px]">Date</TableHead>
            <TableHead className="w-[140px] text-right">Total</TableHead>
            <TableHead>Asked</TableHead>
            <TableHead>{history ? 'Decision' : 'State'}</TableHead>
            {history ? null : <TableHead className="w-[260px] text-right">Action</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((a) => (
            <TableRow key={a.id} data-testid="approval-row">
              <TableCell>
                <Link
                  className="text-primary hover:underline"
                  to={documentPath(a.document.kind, a.document.id)}
                >
                  {a.document.kind === 'invoice' ? 'Invoice' : 'Bill'}{' '}
                  {a.document.number ?? '(draft)'}
                </Link>
              </TableCell>
              <TableCell>{a.document.contactName}</TableCell>
              <TableCell className="tnum">{formatDate(a.document.issueDate)}</TableCell>
              <TableCell className="tnum text-right">
                {a.document.currency} {formatAmount(a.document.total)}
              </TableCell>
              <TableCell className="max-w-[320px] whitespace-normal text-sm">
                {a.requestedBy} · {formatDateTime(a.requestedAt)}
                {a.note ? <div className="text-muted-foreground">{a.note}</div> : null}
              </TableCell>
              <TableCell className="max-w-[260px] whitespace-normal text-sm">
                <span className={cn(a.status === 'rejected' && 'text-destructive')}>
                  {approvalState(a)}
                </span>
                {a.decidedBy ? (
                  <div className="text-muted-foreground">
                    {a.decidedBy}
                    {a.decidedAt ? ` · ${formatDateTime(a.decidedAt)}` : ''}
                  </div>
                ) : null}
                {a.decisionComment ? <div>“{a.decisionComment}”</div> : null}
              </TableCell>
              {history ? null : (
                <TableCell className="text-right">
                  {a.readyToPost && staff ? (
                    <Button size="sm" asChild>
                      <Link to={documentPath(a.document.kind, a.document.id)}>Open to post</Link>
                    </Button>
                  ) : (
                    <ApprovalActions a={a} />
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
